import { UploadedFile } from "../../../components/FileUploadSection";
import { User } from "../../../pages/companyProfile/tabComponents/user/UserTab";
import { Client } from "../../../pages/masterData/client/types";
import {
  Cleaner,
  Driver,
  Lady,
  Manager,
  Route,
  Vehicle,
} from "../../../services/api";
import { ContractFormData } from "./contract";

// Export route schedule types
export * from "./routeSchedule";

// Export contract types
export * from "./contract";

// Project status enum - Updated to match backend API
export enum ProjectStatus {
  STARTED = "STARTED",
  ON_HOLD = "ON_HOLD",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  TERMINATED = "TERMINATED",
  UPCOMING = "UPCOMING",
}

// Allocation category enum - Updated to match backend API
export enum AllocationCategory {
  VEHICLE = "VEHICLE",
  RESOURCE = "RESOURCE",
  MASTER = "MASTER",
  ROUTE = "ROUTE",
  CLIENT = "CLIENT",
  OTHER = "OTHER",
}

export enum AllocationSubCategory {
  DRIVER = "DRIVER",
  CLEANER = "CLEANER",
  MANAGER = "MANAGER",
  AREA_MANAGER = "AREA_MANAGER",
  CLUSTER_LEADER = "CLUSTER_LEADER",
  LADY = "LADY",
  ROUTE = "ROUTE",
  OTHER = "OTHER",
  BUS = "BUS",
  TRUCK = "TRUCK",
  EQUIPMENT = "EQUIPMENT",
}

// Allocation status enum - Updated to match backend API
export enum AllocationStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  SUSPENDED = "SUSPENDED",
}

// Tab enum for project details
export enum ProjectDetailTabs {
  SUMMARY = "SUMMARY",
  LOGS = "LOGS",
  INCOME = "INCOME",
  EXPENSE = "EXPENSE",
  ALLOCATIONS = "ALLOCATIONS",
  DOCUMENTS = "DOCUMENTS",
  CONTRACT = "CONTRACT",
  USERS = "USERS",
  DIVISIONS = "DIVISIONS",
}

// Main Project interface - Updated to match backend API
export interface Project {
  id: string;
  name: string;
  description?: string;
  status: ProjectStatus;
  startDate: string;
  endDate?: string;
  durationInMonths?: string;
  clientId: string;
  companyId: string;
  createdBy: string;
  createdByUser?: User;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  // Additional fields for frontend display
  client?: Client; // Client name for display
  updatedBy?: string; // Updated by for display
  allocations?: Allocation[];
  userTabs?: string[]; // User's accessible tabs
}

export interface Summary {
  totalIncomes: number;
  expectedIncomes: number;
  totalIncomeCount: number;
  difference?: number;
  totalExpenses: number;
  totalExpenseCount: number;
  totalAllocations: number;
  allocationSummary: AllocationSummaryArray;
}

// Allocation interface - Updated to match backend API
export interface Allocation {
  id: string;
  category: AllocationCategory;
  categoryId: string; // ID of the allocated resource
  // Data of the allocated resource based on the category
  vehicleData?: Vehicle;
  staffData?: Driver | Cleaner | Manager | Lady;
  routeData?: Route;
  assetData?: any; // Asset data for asset allocations
  subCategory: string; // Sub-category specification
  status: AllocationStatus;
  startDate: string;
  endDate?: string | null;
  durationInMonths?: string;
  meta?: Record<string, any>; // Additional metadata (JSON)
  projectId: string;
  companyId: string;
  createdBy: string;
  createdByUser?: User;
  createdAt: string;
  deletedAt?: string;
  // Additional fields for frontend display
  categoryData?: any; // Reference data object when available
  allocationTags?: AllocationTag[];
}

export interface AllocationSummary {
  [key: string]: {
    count: number;
    activeCount: number;
  };
}

export type AllocationSummaryArray = Array<{
  [key in AllocationCategory]?: {
    count: number;
    activeCount: number;
  };
}>;

// AllocationTag interface - New from backend API
export interface AllocationTag {
  id?: string;
  category1Type: string; // Type of first entity
  category1Id: string; // ID of first entity
  category2Type: string; // Type of second entity
  category2Id: string; // ID of second entity
  startDate: string;
  endDate?: string | null;
  companyId?: string;
  createdBy?: string;
  createdByUser?: User;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  category2Info?: any;
  schedules?: any;
}

// API Response interface
export interface ApiResponse<T> {
  isSuccess: boolean;
  data?: T;
  error?: string;
  details?: Record<string, string>; // For validation errors
}

// Request interfaces for API - Updated to match backend
export interface CreateProjectRequest {
  name: string;
  description?: string;
  startDate: string;
  endDate?: string | null;
  durationInMonths?: string;
  clientId: string;
  client?: Client;
  status?: ProjectStatus;
}

export interface UpdateProjectRequest {
  id?: string;
  contractId?: string;
  name?: string;
  description?: string;
  startDate?: string;
  endDate?: string | null;
  durationInMonths?: string;
  clientId?: string;
  client?: Client;
  status?: ProjectStatus;
}

export interface CreateAllocationRequest {
  category: AllocationCategory;
  categoryId: string;
  subCategory: string;
  startDate: string;
  endDate?: string | null;
  durationInMonths?: string;
  projectId: string;
  meta?: Record<string, any>;
  createdBy?: string;
  companyId?: string;
}

export interface CreateAllocationWithTagsRequest
  extends CreateAllocationRequest {
  allocationTags?: CreateAllocationTagRequest[];
}

export interface UpdateAllocationRequest {
  category?: AllocationCategory;
  categoryId?: string;
  subCategory?: string;
  status?: AllocationStatus;
  startDate?: string;
  endDate?: string | null;
  meta?: Record<string, any>;
}

export interface CreateAllocationTagRequest {
  category1Type: string;
  category1Id: string;
  category2Type: string;
  category2Id: string;
  startDate: string;
  endDate?: string | null;
}

export interface UpdateAllocationTagRequest {
  category1Type?: string;
  category1Id?: string;
  category2Type?: string;
  category2Id?: string;
  startDate?: string;
  endDate?: string | null;
}

// Filter interfaces
export interface ProjectFilter {
  limit?: number;
  offset?: number;
  status?: ProjectStatus[] | ProjectStatus.STARTED;
  search?: string;
  clientId?: string;
  startDate?: string;
  endDate?: string | null;
}

export interface AllocationFilter {
  limit?: number;
  offset?: number;
  category?: AllocationCategory;
  status?: AllocationStatus;
  projectId?: string;
  startDate?: string;
  endDate?: string;
}

export interface AllocationTagFilter {
  limit?: number;
  offset?: number;
  category1Type?: string;
  category1Id?: string;
  category2Type?: string;
  category2Id?: string;
  startDate?: string;
  endDate?: string;
}

// Legacy interfaces for backward compatibility
export interface ProjectModule extends Project {
  // Additional legacy fields
  updatedBy: string;
}

export interface SuperProject extends Project {
  contract?: ContractFormData;
  documents?: UploadedFile[];
  onDivisionBasis?: boolean;
  divisions?: {
    name: string;
    description: string;
  }[];
  updatedBy?: string;
}

// Individual meta item interface - Legacy support
export interface MetaItem {
  type: string;
  id?: string;
  value: any;
  [key: string]: any;
}

// Project Notes interface
export interface ProjectNote {
  id: string;
  projectId: string;
  note: string;
  createdBy: string;
  createdByUser?: User;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

// Project Activity Log interface
export interface ProjectActivityLog {
  id: string;
  projectId: string;
  companyId: string;
  description: string;
  creator?: User;
  createdBy: string;
  createdAt: string;
}

// Project Expense interface
export interface ProjectExpense {
  id: string;
  projectId: string;
  description: string;
  amount: number;
  expenseDate: string;
  category?: string;
  createdBy: string;
  createdByUser?: User;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

// Project Income interface
export interface ProjectIncome {
  id: string;
  projectId: string;
  description: string;
  amount: number;
  incomeDate: string;
  category?: string;
  createdBy: string;
  createdByUser?: User;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

// Resource allocation specific interfaces - Legacy support
export interface ResourceAllocation extends Allocation {
  category: AllocationCategory.RESOURCE;
  subCategory: "DRIVER" | "CLEANER" | "MANAGER" | "LADY" | "Area Manager" | "Cluster Leader";
}

export interface VehicleAllocation extends Allocation {
  category: AllocationCategory.VEHICLE;
  subCategory: "BUS" | "TRUCK";
  referenceData?: any;
  durationMonths?: string;
}
export interface RouteAllocation extends Allocation {
  category: AllocationCategory.ROUTE;
  subCategory: "SCHEDULE";
  referenceData?: any;
  durationMonths?: string;
}

export interface MasterDataAllocation extends Allocation {
  category: AllocationCategory.MASTER;
}

export interface TabInfo {
  key: ProjectDetailTabs;
  label: string;
  icon: React.ReactNode;
}

// Master Data Allocation Types
export interface MasterDataAllocation {
  id: string;
  category: AllocationCategory.MASTER;
  categoryId: string;
  subCategory: string;
  projectId: string;
  startDate: string;
  endDate?: string | null;
  masterData: any;
  project: Project;
  meta?: Record<string, any>;
}

export interface CreateMasterDataWithAllocationRequest {
  projectId: string;
  masterDataType: string;
  masterData: any;
  allocationMeta?: {
    priority?: string;
    notes?: string;
  };
}

export interface AllocateExistingMasterDataRequest {
  projectId: string;
  masterDataId: string;
  masterDataType: string;
  allocationMeta?: {
    priority?: string;
    notes?: string;
  };
}

export interface BulkAllocateMasterDataRequest {
  projectId: string;
  allocations: Array<{
    masterDataId: string;
    masterDataType: string;
    allocationMeta?: {
      priority?: string;
      notes?: string;
    };
  }>;
}

export interface MasterDataAllocationResponse {
  masterData: any;
  allocation: MasterDataAllocation;
}

export interface BulkAllocationResult {
  masterDataId: string;
  masterDataType: string;
  status: "created" | "skipped" | "error";
  allocation?: MasterDataAllocation;
  message?: string;
}

// Master Data Types Enum
export enum MasterDataType {
  CLIENT = "client",
  PARTY = "party",
  ROUTE = "route",
  GARAGE = "garage",
  MECHANIC = "mechanic",
  PARKING = "parking",
  PETROLPUMP = "petrolpump",
  MARKETLOAD = "marketload",
  BRANCH = "branch",
  PICKUPMAN = "pickupman",
  STOPS = "stops",
}

// Master Data Field Requirements
export interface ClientMasterData {
  clientName: string;
  primaryContactName: string;
  primaryContactNumber: string;
  email?: string;
  address?: string;
}

export interface PartyMasterData {
  partyName: string;
  primaryContactName: string;
  primaryContactNumber: string;
  email?: string;
  address?: string;
}

export interface RouteMasterData {
  name: string;
  fromLocation: string;
  toLocation: string;
  routeManagerName?: string;
  routeManagerNumber?: string;
}

export interface GarageMasterData {
  name: string;
  location: string;
  contactNumber?: string;
}

export interface MechanicMasterData {
  name: string;
  contactNumber: string;
  specialization?: string;
}

export interface ParkingMasterData {
  name: string;
  location: string;
  contactNumber?: string;
}

export interface PetrolPumpMasterData {
  petrolPumpName: string;
  location: string;
  contactNumber?: string;
}

export interface MarketLoadMasterData {
  name: string;
  location: string;
  description?: string;
}

export interface BranchMasterData {
  name: string;
  location: string;
  contactNumber?: string;
}

export interface PickupManMasterData {
  name: string;
  contactNumber: string;
  location?: string;
}

export interface StopsMasterData {
  name: string;
  location: string;
}


export interface CreateSuperProjectRequest {
  name: string;
  description?: string;
  startDate: string;
  endDate?: string | null;
  durationInMonths?: string;
  clientId: string;
  client?: Client;
  status?: ProjectStatus;
  contractId?: string;
  contract?: ContractFormData;
  documents?: UploadedFile[];
  onDivisionBasis?: boolean;
  divisions?: {
    name: string;
    description: string;
  }[];
}
